import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import NavBar from "@/components/site/NavBar";
import Footer from "@/components/site/Footer";
import {
  PrivacyPolicyBody,
  TermsOfServiceBody,
} from "@/components/site/policyContent";
import { ServicesBody } from "@/components/site/servicesContent";

/**
 * Canonical URLs carry the trailing slash on purpose. The prerenderer writes
 * these routes as directories (build/services/index.html), so Apache's mod_dir
 * 301-redirects /services to /services/ before serving. The slashed form is the
 * one that resolves without a hop, so it is what we advertise — and it must
 * match public/sitemap.xml and public/llms.txt exactly.
 */
const PAGES = {
  privacy: {
    title: "Privacy Policy",
    documentTitle: "Privacy Policy — OneVidaria Digital",
    description:
      "How OneVidaria Digital collects, uses, and protects your information across its ten applications.",
    canonical: "https://onevidariadigital.com/privacy/",
    Body: PrivacyPolicyBody,
    testId: "privacy-page",
    dated: true,
  },
  terms: {
    title: "Terms of Service",
    documentTitle: "Terms of Service — OneVidaria Digital",
    description:
      "The terms governing your use of applications developed and operated by OneVidaria Digital.",
    canonical: "https://onevidariadigital.com/terms/",
    Body: TermsOfServiceBody,
    testId: "terms-page",
    dated: true,
  },
  services: {
    title: "Services",
    documentTitle: "Services — OneVidaria Digital",
    description:
      "One-page website builds from $800, website maintenance from $99 a month, and social setup from $199. Client work that funds the product studio.",
    canonical: "https://onevidariadigital.com/services/",
    Body: ServicesBody,
    testId: "services-page",
    dated: false,
  },
};

// Terms §8 promises this moves on any material change to either policy. Bump
// it in the same commit as the content change, not after.
const LAST_UPDATED = "4 November 2026";

/**
 * Stable, linkable document page. Policy prose comes from the same module the
 * footer modals render, so the two cannot drift; the services page reuses the
 * same shell so every routed page shares one layout.
 */
export default function PolicyPage({ kind }) {
  const page = PAGES[kind];

  // Head tags live in the static public/index.html, and the prerenderer copies
  // that head verbatim onto every route — so anything not patched here ships
  // with the homepage's value. Canonical is the one that matters: left alone it
  // tells search engines these routes are duplicates of /, which un-indexes the
  // page. Restoring on unmount keeps client-side navigation back to / honest.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = page.documentTitle;

    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute("content");
    meta?.setAttribute("content", page.description);

    const canonical = document.querySelector('link[rel="canonical"]');
    const previousCanonical = canonical?.getAttribute("href");
    canonical?.setAttribute("href", page.canonical);

    const ogUrl = document.querySelector('meta[property="og:url"]');
    const previousOgUrl = ogUrl?.getAttribute("content");
    ogUrl?.setAttribute("content", page.canonical);

    return () => {
      document.title = previousTitle;
      if (previousDescription) meta?.setAttribute("content", previousDescription);
      if (previousCanonical) canonical?.setAttribute("href", previousCanonical);
      if (previousOgUrl) ogUrl?.setAttribute("content", previousOgUrl);
    };
  }, [page]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [kind]);

  const { Body } = page;

  return (
    <div
      data-testid={page.testId}
      className="relative min-h-screen bg-surface-0 text-[#F8FAFC] selection:bg-[#10B981]/30"
    >
      <NavBar />
      <main
        className={`mx-auto px-6 lg:px-10 pt-32 pb-24 ${
          kind === "services" ? "max-w-5xl" : "max-w-3xl"
        }`}
      >
        <Link
          to="/"
          data-testid="policy-back-link"
          className="inline-flex min-h-[44px] items-center gap-2 rounded-sm text-sm text-white/65 transition-colors hover:text-white"
        >
          <ArrowLeft size={15} />
          Back to the site
        </Link>

        <h1
          className={
            kind === "services"
              ? "font-serif-display mt-8 text-4xl font-medium tracking-tight text-cream sm:text-5xl lg:text-6xl"
              : "font-display mt-8 text-4xl sm:text-5xl font-normal tracking-tight text-white"
          }
        >
          {page.title}
        </h1>
        {kind === "services" ? (
          <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-white/70">
            Small client projects fund the product studio — and keep us close
            to the problems real businesses have on the web.
          </p>
        ) : null}
        {page.dated ? (
          <p className="mt-3 text-sm text-white/65">
            Last updated {LAST_UPDATED}
          </p>
        ) : null}

        <div className="mt-10">
          <Body />
        </div>
      </main>
      <Footer />
    </div>
  );
}
